import "@/index.css";
import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/sonner";
import { installClientErrorBeacon } from "@/lib/errorBeacon";
import { registerServiceWorker } from "@/lib/registerSW";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";

// Route-level code splitting — each page ships as its own JS chunk. At 100M
// users this is table-stakes: a first-time visitor to /login should not
// download the admin sponsors dashboard bundle.
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const OfferFirstSignupPage = lazy(() => import("@/pages/OfferFirstSignupPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const NewDreamPage = lazy(() => import("@/pages/NewDreamPage"));
const DreamDetailPage = lazy(() => import("@/pages/DreamDetailPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const TalkPage = lazy(() => import("@/pages/TalkPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const MyValuePage = lazy(() => import("@/pages/MyValuePage"));
const CricketPage = lazy(() => import("@/pages/CricketPage"));
const PlayHubPage = lazy(() => import("@/pages/PlayHubPage"));
const GamingPage = lazy(() => import("@/pages/GamingPage"));
const FootballPage = lazy(() => import("@/pages/FootballPage"));
const AdminSponsorsPage = lazy(() => import("@/pages/AdminSponsorsPage"));
const ForBrandsPage = lazy(() => import("@/pages/ForBrandsPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const ForTalentPage = lazy(() => import("@/pages/ForTalentPage"));

// Install once at module load — captures errors from React hydration onward.
installClientErrorBeacon();
// Register the PWA service worker (production only — dev caching is a footgun).
registerServiceWorker();

function RouteFallback() {
    return (
        <div
            className="min-h-screen bg-white flex items-center justify-center text-slate-500"
            data-testid="route-loading"
        >
            Summoning…
        </div>
    );
}

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center text-slate-500" data-testid="auth-loading">
                Summoning Geenie…
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function PublicOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/" replace />;
    return children;
}

function AppRoutes() {
    const location = useLocation();
    // Synchronous detection of OAuth return — handle BEFORE protected routing.
    if (location.hash && location.hash.includes("session_id=")) {
        return (
            <Suspense fallback={<RouteFallback />}>
                <AuthCallback />
            </Suspense>
        );
    }
    return (
        <Suspense fallback={<RouteFallback />}>
            <Routes>
                <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
                <Route path="/signup" element={<PublicOnly><OfferFirstSignupPage /></PublicOnly>} />
                <Route path="/signup/classic" element={<PublicOnly><SignupPage /></PublicOnly>} />
                <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
                <Route path="/reset-password" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
                <Route path="/" element={<Protected><DashboardPage /></Protected>} />
                <Route path="/new" element={<Protected><NewDreamPage /></Protected>} />
                <Route path="/dream/:id" element={<Protected><DreamDetailPage /></Protected>} />
                <Route path="/chat" element={<Protected><ChatPage /></Protected>} />
                <Route path="/talk" element={<Protected><TalkPage /></Protected>} />
                <Route path="/value" element={<Protected><MyValuePage /></Protected>} />
                <Route path="/play" element={<Protected><PlayHubPage /></Protected>} />
                <Route path="/play/gaming" element={<Protected><GamingPage /></Protected>} />
                <Route path="/play/football" element={<Protected><FootballPage /></Protected>} />
                <Route path="/play/cricket" element={<Protected><CricketPage /></Protected>} />
                <Route path="/admin/sponsors" element={<Protected><AdminSponsorsPage /></Protected>} />
                <Route path="/for-brands" element={<Protected><ForBrandsPage /></Protected>} />
                <Route path="/careers" element={<Protected><CareersPage /></Protected>} />
                <Route path="/for-talent" element={<Protected><ForTalentPage /></Protected>} />
                <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <ThemeProvider>
                    <AuthProvider>
                        <AppRoutes />
                        <PWAInstallPrompt />
                        <Toaster position="top-center" richColors closeButton />
                    </AuthProvider>
                </ThemeProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}
